import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from './ui/alert-dialog';
import { AlertTriangle, Calendar, ExternalLink } from 'lucide-react';

/**
 * Read-only clash list shown after approving leave or saving leave entries.
 *
 * Props:
 *   open     — boolean
 *   onClose  — () => void
 *   clashes  — array of { job_number, job_id, customer_name, start_date, end_date, is_part_day }
 */
export default function LeaveClashDialog({ open, onClose, clashes }) {
  const items = clashes || [];

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <AlertDialogContent className="max-w-lg" data-testid="leave-clash-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600" data-testid="leave-clash-title">
            <AlertTriangle className="h-5 w-5" />
            This leave clashes with existing allocations
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="max-h-[400px] overflow-y-auto space-y-2 mt-2">
              {items.map((clash, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 dark:bg-amber-950/20 rounded-lg text-sm"
                  data-testid={`clash-row-${i}`}
                >
                  <Calendar className="h-4 w-4 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {clash.job_number ? (
                        <a
                          href={`/schedule?editJob=${clash.job_id || ''}&date=${clash.start_date || ''}`}
                          className="font-medium text-amber-800 dark:text-amber-200 hover:underline inline-flex items-center gap-1"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {clash.job_number}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="font-medium text-amber-800 dark:text-amber-200">Job</span>
                      )}
                      {clash.customer_name && (
                        <span className="text-muted-foreground truncate">— {clash.customer_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span>{clash.start_date} — {clash.end_date || clash.start_date}</span>
                      {clash.part_day && (
                        <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase">
                          part day
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No clashes found.</p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose} data-testid="leave-clash-ok">
            OK
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
